import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService, EventsService, CustomerService, ResourceService, AttendanceNoteService, CarouselService, WeatherService, FileService, InfoScreenService, EmployeeService } from '../../services';
import { Spinner, Breadcrumb, BreadcrumbItem, Tabs, Tab, Modal, Button, Carousel, Form } from 'react-bootstrap';
import { PencilSquare, FileImage, ArrowsFullscreen, GeoAlt, Trash } from 'react-bootstrap-icons';
import moment from 'moment';
import { expandCalendarRecurrence } from '../../shared/recurrence.utils';
import { getCookieValue } from '../../shared/manageTablePersistence';
import InfoScreen2Layout from './InfoScreen2Layout';
import InfoScreen3Layout from './InfoScreen3Layout';
import {
  buildScreen2MealSections,
  buildScreen3MealSections,
  formatInfoScreenAppointmentCustomerName,
  formatInfoScreenDriverName,
  formatInfoScreenDriverNameWithPickupTime,
  getInfoScreenAppointmentDay,
  parseDriverPickupTime24,
  SCREEN2_ACTIVITIES_PAGE_SIZE,
  SCREEN2_ACTIVITIES_PAGE_SWITCH_MS,
  SCREEN2_APPOINTMENTS_PAGE_SIZE,
  SCREEN3_ACTIVITIES_PAGE_SIZE,
  SCREEN3_APPOINTMENTS_PAGE_SIZE,
} from './infoScreen2.utils';

/** Legacy meal text embedded in description (Breakfast: … Lunch: … Snack: …). */
function parseMealMenuFromDescription(description) {
	const d = description || '';
	let snack = '';
	if (d.includes('Snack') || d.includes('snack')) {
		const snackParts = d.split(/Snack|snack/);
		if (snackParts.length > 1) snack = snackParts[1].replace(/:/g, '').trim();
	}
	let lunch = '';
	const firstPart = d.split(/Snack|snack/)[0];
	if (firstPart.includes('Lunch') || firstPart.includes('lunch')) {
		const lunchParts = firstPart.split(/Lunch|lunch/);
		if (lunchParts.length > 1) lunch = lunchParts[1].replace(/:/g, '').trim();
	}
	let breakfast = '';
	const secondPart = firstPart.split(/Lunch|lunch/)[0];
	if (secondPart.includes('Breakfast') || secondPart.includes('breakfast')) {
		const breakfastParts = secondPart.split(/Breakfast|breakfast/);
		if (breakfastParts.length > 1) breakfast = breakfastParts[1].replace(/:/g, '').trim();
	}
	return { breakfast, lunch, snack };
}

const MEAL_PLAN_TYPES = new Set(['breakfast', 'lunch', 'snack']);

function stackedLocalizedLines(...parts) {
	return parts.map((part) => `${part ?? ''}`.trim()).filter(Boolean).join('\n');
}

function formatMealDishBlock(item) {
	const names = stackedLocalizedLines(item?.title, item?.title_cn, item?.title_vn);
	const ingredients = `${item?.ingredients || ''}`.trim();
	return stackedLocalizedLines(names, ingredients);
}

const CAROUSEL_MAX_IMAGES = 12;
const FULLSCREEN_APPOINTMENTS_PAGE_SIZE = 12;
const FULLSCREEN_APPOINTMENTS_PAGE_SWITCH_MS = 10 * 1000;
const INFO_SCREEN_WEATHER_CACHE_COOKIE = 'info_screen_weather_cache';
const INFO_SCREEN_WEATHER_CACHE_MAX_AGE_SEC = 12 * 60 * 60;

const setCookieValueWithMaxAge = (name, value, maxAgeSec) => {
  if (typeof document === 'undefined') return;
  const secure =
    typeof window !== 'undefined' && window.location?.protocol === 'https:'
      ? '; Secure'
      : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax; Max-Age=${maxAgeSec}${secure}`;
};

/**
 * One table row for today: columns are joined strings per meal type.
 * Uses calendar fields meal_type + title + ingredients; falls back to legacy description parsing.
 */
function buildTodayMealMenuRows(todayMealPlanEvents) {
	const buckets = { breakfast: [], lunch: [], snack: [] };

	for (const item of todayMealPlanEvents) {
		const type = String(item.meal_type || '').trim().toLowerCase();
		const line = formatMealDishBlock(item);

		if (MEAL_PLAN_TYPES.has(type) && line) {
			buckets[type].push(line);
			continue;
		}

		const parsed = parseMealMenuFromDescription(item.description || '');
		let anyParsed = false;
		if (parsed.breakfast) {
			buckets.breakfast.push(parsed.breakfast);
			anyParsed = true;
		}
		if (parsed.lunch) {
			buckets.lunch.push(parsed.lunch);
			anyParsed = true;
		}
		if (parsed.snack) {
			buckets.snack.push(parsed.snack);
			anyParsed = true;
		}
		if (anyParsed) continue;

		const fallback = formatMealDishBlock(item) || String(item.title || '').trim();
		if (fallback) {
			if (type === 'lunch') buckets.lunch.push(fallback);
			else if (type === 'snack') buckets.snack.push(fallback);
			else buckets.breakfast.push(fallback);
		}
	}

	const breakfast = buckets.breakfast.join('\n\n');
	const lunch = buckets.lunch.join('\n\n');
	const snack = buckets.snack.join('\n\n');
	if (!breakfast && !lunch && !snack) return [];
	return [{ id: 'info-screen-meal-menu-today', breakfast, lunch, snack }];
}

const DEFAULT_INFO_BACKGROUND = '/images/background.jpg';

const InfoScreen = () => {
  const navigate = useNavigate();
  const canEditInfoScreen = AuthService.canEditInfoScreen();
  const canViewAppointmentCalendar = AuthService.hasPermission('View_Appointment Calendar');
  const [isLoading, setIsLoading] = useState(true);
  const [medicalEvents, setMedicalEvents] = useState([]);
  const [activityEvents, setActivityEvents] = useState([]);
  const [mealPlanEvents, setMealPlanEvents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [driverPeople, setDriverPeople] = useState([]);
  const [resources, setResources] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // AttendanceNote state
  const [showEditModal, setShowEditModal] = useState(false);
  const [attendanceNote, setAttendanceNote] = useState({
    id: null,
    slogan: 'Quality | Commitment',
    introduction: 'At Worldshine, we provide medical day care services to seniors who need assistance with activities of daily life (ADLs). Our experienced and highly trained staff are well qualified to care for seniors suffering from a variety of ailments inckluding dementia, Alzheimer\'s and other physical disabilities.',
    image: null,
    imageFileName: null
  });
  const [editForm, setEditForm] = useState({
    slogan: '',
    introduction: '',
    image: null
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDeletingAttendanceImage, setIsDeletingAttendanceImage] = useState(false);
  const [isDeletingCarouselFile, setIsDeletingCarouselFile] = useState(false);

  // Gallery state
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryImages, setGalleryImages] = useState([
    '/images/background.jpg',
    '/images/landing.png',

  ]);
  const [galleryForm, setGalleryForm] = useState([]);

  // Carousel state — files: server uploads [{ url, name }] for delete; images: display URLs
  const [carousel, setCarousel] = useState({
    id: null,
    images: [
      '/images/background.jpg',
      '/images/landing.png',
    ],
    files: []
  });
  /** @type {Array<{ preview: string, serverFileName?: string, pendingFile?: File }>} */
  const [carouselForm, setCarouselForm] = useState([]);

  // Background and full screen state (committed URL survives refresh; backgroundImage may be a data URL while editing)
  const [committedBackgroundUrl, setCommittedBackgroundUrl] = useState(DEFAULT_INFO_BACKGROUND);
  const [backgroundImage, setBackgroundImage] = useState(DEFAULT_INFO_BACKGROUND);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [activeInfoTab, setActiveInfoTab] = useState('infoScreen');
  const [fullscreenLayout, setFullscreenLayout] = useState('infoScreen');
  const [noticeText, setNoticeText] = useState('');
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [noticeDraft, setNoticeDraft] = useState('');
  const [screen2ActivitiesPage, setScreen2ActivitiesPage] = useState(1);
  const [screen2AppointmentsPage, setScreen2AppointmentsPage] = useState(1);
  const [screen3ActivitiesPage, setScreen3ActivitiesPage] = useState(1);
  const [screen3AppointmentsPage, setScreen3AppointmentsPage] = useState(1);
  const [showBackgroundModal, setShowBackgroundModal] = useState(false);
  const [selectedBackgroundFile, setSelectedBackgroundFile] = useState(null);

  const [weatherPayload, setWeatherPayload] = useState(null);
  const [showWeatherZipModal, setShowWeatherZipModal] = useState(false);
  const [weatherZipDraft, setWeatherZipDraft] = useState('');
  const [fullscreenAppointmentsPage, setFullscreenAppointmentsPage] = useState(1);

  const expandRecurrence = (rule, rangeFrom, rangeTo) =>
    expandCalendarRecurrence(rule, rangeFrom, rangeTo);

  const redirectTo = () => {
    navigate('/dashboard/dashboard');
  }

  const loadWeatherSnapshot = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cachedRaw = getCookieValue(INFO_SCREEN_WEATHER_CACHE_COOKIE);
      if (cachedRaw) {
        try {
          const cachedPayload = JSON.parse(cachedRaw);
          if (cachedPayload && typeof cachedPayload === 'object') {
            setWeatherPayload(cachedPayload);
            return;
          }
        } catch (e) {
          // Ignore malformed cookie payload and fetch fresh weather.
        }
      }
    }

    try {
      const { data } = await WeatherService.get();
      setWeatherPayload(data);
      setCookieValueWithMaxAge(
        INFO_SCREEN_WEATHER_CACHE_COOKIE,
        JSON.stringify(data),
        INFO_SCREEN_WEATHER_CACHE_MAX_AGE_SEC
      );
    } catch (err) {
      console.error('Weather load failed:', err);
      setWeatherPayload(null);
    }
  }, []);

  useEffect(() => {
    if (!AuthService.canViewInfoScreen()) {
      window.alert('You haven\'t login yet OR this user does not have access to this page. Please change an admin account to login.');
      AuthService.logout();
      navigate('/login');
      return;
    }
    
    // Load customers and resources
    CustomerService.getAllCustomers().then((data) => {
      setCustomers(data.data);
    });

    Promise.all([
      EmployeeService.getAllEmployees().then((res) => res?.data).catch(() => []),
      EmployeeService.getExternalUserPermissionsList(EventsService.site).then((res) => res?.data).catch(() => []),
    ]).then(([employees, hrUsers]) => {
      setDriverPeople([
        ...(Array.isArray(employees) ? employees : []),
        ...(Array.isArray(hrUsers) ? hrUsers : []),
      ]);
    });
    
    ResourceService.getAll().then(data => {
      setResources(data.data);
    });
    
    // Load attendance note and carousel
    loadAttendanceNote();
    loadCarousel();
    (async () => {
      try {
        const { data } = await InfoScreenService.getNotice();
        setNoticeText(typeof data?.text === 'string' ? data.text : '');
      } catch (e) {
        console.error('Error loading info screen notice:', e);
      }
    })();
    (async () => {
      try {
        const { data } = await InfoScreenService.getBackground();
        const raw = data?.url;
        const display = raw
          ? InfoScreenService.resolveUploadedFileUrl(raw) || DEFAULT_INFO_BACKGROUND
          : DEFAULT_INFO_BACKGROUND;
        setCommittedBackgroundUrl(display);
        setBackgroundImage(display);
      } catch (e) {
        console.error('Error loading info screen background:', e);
      }
    })();
    loadWeatherSnapshot();
    
    setIsLoading(false);
  }, [navigate, loadWeatherSnapshot]);

  useEffect(() => {
    const id = window.setInterval(() => {
      loadWeatherSnapshot();
    }, 10 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [loadWeatherSnapshot]);

  const appointmentDay = getInfoScreenAppointmentDay(currentTime);
  const visibleMedicalEvents = (medicalEvents || []).filter((item) =>
    moment(item.start_time).isSame(appointmentDay, 'day')
  );
  const appointmentEmptyMessage = moment(appointmentDay).isSame(moment(currentTime), 'day')
    ? 'No medical appointments scheduled for today'
    : 'No medical appointments scheduled for tomorrow';
  const activityEmptyMessage = moment(appointmentDay).isSame(moment(currentTime), 'day')
    ? 'No activities scheduled for today'
    : 'No activities scheduled for tomorrow';
  const mealEmptyMessage = moment(appointmentDay).isSame(moment(currentTime), 'day')
    ? 'No meal plan for today'
    : 'No meal plan for tomorrow';
  const appointmentTitleSuffix = moment(appointmentDay).isSame(moment(currentTime), 'day')
    ? ''
    : ' (tomorrow)';
  const visibleActivityEvents = (activityEvents || []).filter((item) =>
    moment(item.start_time).isSame(appointmentDay, 'day')
  );
  const visibleMealPlans = (mealPlanEvents || []).filter((item) =>
    moment(item.start_time).isSame(appointmentDay, 'day')
  );
  const mealEvents = buildTodayMealMenuRows(visibleMealPlans);
  const screen2MealSections = buildScreen2MealSections(visibleMealPlans);
  const screen3MealSections = buildScreen3MealSections(visibleMealPlans);

  const fullscreenAppointmentsTotalPages = Math.max(
    1,
    Math.ceil((visibleMedicalEvents?.length || 0) / FULLSCREEN_APPOINTMENTS_PAGE_SIZE)
  );

  const fullscreenPagedMedicalEvents = (visibleMedicalEvents || []).slice(
    (fullscreenAppointmentsPage - 1) * FULLSCREEN_APPOINTMENTS_PAGE_SIZE,
    fullscreenAppointmentsPage * FULLSCREEN_APPOINTMENTS_PAGE_SIZE
  );

  const screen2ActivitiesTotalPages = Math.max(
    1,
    Math.ceil((visibleActivityEvents?.length || 0) / SCREEN2_ACTIVITIES_PAGE_SIZE)
  );

  const screen2PagedActivityEvents = (visibleActivityEvents || []).slice(
    (screen2ActivitiesPage - 1) * SCREEN2_ACTIVITIES_PAGE_SIZE,
    screen2ActivitiesPage * SCREEN2_ACTIVITIES_PAGE_SIZE
  );

  const screen2AppointmentsTotalPages = Math.max(
    1,
    Math.ceil((visibleMedicalEvents?.length || 0) / SCREEN2_APPOINTMENTS_PAGE_SIZE)
  );

  const screen2MedicalEventsByPickup = [...(visibleMedicalEvents || [])].sort((a, b) => {
    const pickupA = `${a?.driverPickupTime24 || ''}`.trim();
    const pickupB = `${b?.driverPickupTime24 || ''}`.trim();
    if (pickupA && pickupB && pickupA !== pickupB) return pickupA.localeCompare(pickupB);
    if (pickupA && !pickupB) return -1;
    if (!pickupA && pickupB) return 1;
    return moment(a?.start_time).valueOf() - moment(b?.start_time).valueOf();
  });

  const screen2PagedMedicalEvents = screen2MedicalEventsByPickup.slice(
    (screen2AppointmentsPage - 1) * SCREEN2_APPOINTMENTS_PAGE_SIZE,
    screen2AppointmentsPage * SCREEN2_APPOINTMENTS_PAGE_SIZE
  );

  const screen3ActivitiesTotalPages = Math.max(
    1,
    Math.ceil((visibleActivityEvents?.length || 0) / SCREEN3_ACTIVITIES_PAGE_SIZE)
  );

  const screen3PagedActivityEvents = (visibleActivityEvents || []).slice(
    (screen3ActivitiesPage - 1) * SCREEN3_ACTIVITIES_PAGE_SIZE,
    screen3ActivitiesPage * SCREEN3_ACTIVITIES_PAGE_SIZE
  );

  const screen3AppointmentsTotalPages = Math.max(
    1,
    Math.ceil((visibleMedicalEvents?.length || 0) / SCREEN3_APPOINTMENTS_PAGE_SIZE)
  );

  const screen3PagedMedicalEvents = (visibleMedicalEvents || []).slice(
    (screen3AppointmentsPage - 1) * SCREEN3_APPOINTMENTS_PAGE_SIZE,
    screen3AppointmentsPage * SCREEN3_APPOINTMENTS_PAGE_SIZE
  );

  const isPagedTvVisible =
    (!isFullScreen && (activeInfoTab === 'infoScreen2' || activeInfoTab === 'infoScreen3')) ||
    (isFullScreen && (fullscreenLayout === 'infoScreen2' || fullscreenLayout === 'infoScreen3'));

  const isScreen3PagedVisible =
    (!isFullScreen && activeInfoTab === 'infoScreen3') ||
    (isFullScreen && fullscreenLayout === 'infoScreen3');

  useEffect(() => {
    setFullscreenAppointmentsPage((prev) => Math.min(prev, fullscreenAppointmentsTotalPages));
  }, [fullscreenAppointmentsTotalPages]);

  useEffect(() => {
    if (!isFullScreen || !canViewAppointmentCalendar || fullscreenAppointmentsTotalPages <= 1) return;
    const timerId = window.setInterval(() => {
      setFullscreenAppointmentsPage((prev) =>
        prev >= fullscreenAppointmentsTotalPages ? 1 : prev + 1
      );
    }, FULLSCREEN_APPOINTMENTS_PAGE_SWITCH_MS);
    return () => window.clearInterval(timerId);
  }, [isFullScreen, canViewAppointmentCalendar, fullscreenAppointmentsTotalPages]);

  useEffect(() => {
    if (isFullScreen) {
      setFullscreenAppointmentsPage(1);
    }
  }, [isFullScreen]);

  useEffect(() => {
    setScreen2ActivitiesPage((prev) => Math.min(prev, screen2ActivitiesTotalPages));
  }, [screen2ActivitiesTotalPages]);

  useEffect(() => {
    setScreen2AppointmentsPage((prev) => Math.min(prev, screen2AppointmentsTotalPages));
  }, [screen2AppointmentsTotalPages]);

  useEffect(() => {
    if (!isPagedTvVisible || screen2ActivitiesTotalPages <= 1) return;
    const timerId = window.setInterval(() => {
      setScreen2ActivitiesPage((prev) =>
        prev >= screen2ActivitiesTotalPages ? 1 : prev + 1
      );
    }, SCREEN2_ACTIVITIES_PAGE_SWITCH_MS);
    return () => window.clearInterval(timerId);
  }, [isPagedTvVisible, screen2ActivitiesTotalPages]);

  useEffect(() => {
    if (!isPagedTvVisible || !canViewAppointmentCalendar || screen2AppointmentsTotalPages <= 1) return;
    const timerId = window.setInterval(() => {
      setScreen2AppointmentsPage((prev) =>
        prev >= screen2AppointmentsTotalPages ? 1 : prev + 1
      );
    }, SCREEN2_ACTIVITIES_PAGE_SWITCH_MS);
    return () => window.clearInterval(timerId);
  }, [isPagedTvVisible, canViewAppointmentCalendar, screen2AppointmentsTotalPages]);

  useEffect(() => {
    if (isPagedTvVisible) {
      setScreen2ActivitiesPage(1);
      setScreen2AppointmentsPage(1);
    }
  }, [isPagedTvVisible]);

  useEffect(() => {
    setScreen3ActivitiesPage((prev) => Math.min(prev, screen3ActivitiesTotalPages));
  }, [screen3ActivitiesTotalPages]);

  useEffect(() => {
    setScreen3AppointmentsPage((prev) => Math.min(prev, screen3AppointmentsTotalPages));
  }, [screen3AppointmentsTotalPages]);

  useEffect(() => {
    if (!isScreen3PagedVisible || screen3ActivitiesTotalPages <= 1) return;
    const timerId = window.setInterval(() => {
      setScreen3ActivitiesPage((prev) =>
        prev >= screen3ActivitiesTotalPages ? 1 : prev + 1
      );
    }, SCREEN2_ACTIVITIES_PAGE_SWITCH_MS);
    return () => window.clearInterval(timerId);
  }, [isScreen3PagedVisible, screen3ActivitiesTotalPages]);

  useEffect(() => {
    if (!isScreen3PagedVisible || !canViewAppointmentCalendar || screen3AppointmentsTotalPages <= 1) return;
    const timerId = window.setInterval(() => {
      setScreen3AppointmentsPage((prev) =>
        prev >= screen3AppointmentsTotalPages ? 1 : prev + 1
      );
    }, SCREEN2_ACTIVITIES_PAGE_SWITCH_MS);
    return () => window.clearInterval(timerId);
  }, [isScreen3PagedVisible, canViewAppointmentCalendar, screen3AppointmentsTotalPages]);

  useEffect(() => {
    if (isScreen3PagedVisible) {
      setScreen3ActivitiesPage(1);
      setScreen3AppointmentsPage(1);
    }
  }, [isScreen3PagedVisible]);

  // Load attendance note data
  const loadAttendanceNote = async () => {
    try {
      // Get all attendance notes and filter for active ones
      const response = await AttendanceNoteService.getAll('active');
      const activeNotes = response.data;
      
      if (activeNotes && activeNotes.length > 0) {
        // Sort by create_date and get the latest one
        const sortedNotes = activeNotes.sort((a, b) => 
          new Date(b.create_date) - new Date(a.create_date)
        );
        const latestNote = sortedNotes[0];
        
        // Set the attendance note data
        setAttendanceNote({
          id: latestNote.id,
          slogan: latestNote.slogan,
          introduction: latestNote.introduction,
          image: null,
          imageFileName: null
        });
        
        // Load the image file
        if (latestNote.id) {
          try {
            const imageResponse = await AttendanceNoteService.getAllAttendanceNoteFiles(
              latestNote.id, 
              latestNote.id, 
              'note_img'
            );
            
            console.log('Attendance note image response:', imageResponse);
            
            // Handle both response.data.files and response.data.data.files structures
            const files = imageResponse?.data?.data?.files || imageResponse?.data?.files;
            
            if (files && Array.isArray(files) && files.length > 0) {
              // Get the first image file
              const imageFile = files[0];
              console.log('Attendance note image file:', imageFile);
              setAttendanceNote(prev => ({
                ...prev,
                image: imageFile.url,
                imageFileName: imageFile.name || null
              }));
            } else {
              console.log('No files found in attendance note response');
            }
          } catch (imageError) {
            console.log('No image found for attendance note:', imageError);
          }
        }
      }
    } catch (error) {
      console.error('Error loading attendance note:', error);
    }
  };

  // Load carousel data
  const loadCarousel = async () => {
    try {
      console.log('Loading carousel data...');
      // Get all carousels and filter for active ones
      const response = await CarouselService.getAll('active');
      const activeCarousels = response.data;
      console.log('Active carousels:', activeCarousels);
      
      if (activeCarousels && activeCarousels.length > 0) {
        // Sort by create_date and get the latest one
        const sortedCarousels = activeCarousels.sort((a, b) => 
          new Date(b.create_date) - new Date(a.create_date)
        );
        const latestCarousel = sortedCarousels[0];
        console.log('Latest carousel:', latestCarousel);
        
        // Set the carousel data
        setCarousel({
          id: latestCarousel.id,
          images: [],
          files: []
        });
        
        // Load the carousel images
        if (latestCarousel.id) {
          try {
            console.log('Loading carousel images for ID:', latestCarousel.id);
            const imagesResponse = await CarouselService.getAllCarouselFiles(
              latestCarousel.id, 
              latestCarousel.id, 
              'carousel_item'
            );
            console.log('Carousel images response:', imagesResponse);
            console.log('imagesResponse.data:', imagesResponse?.data);
            console.log('imagesResponse.data?.data:', imagesResponse?.data?.data);
            console.log('imagesResponse.data?.files:', imagesResponse?.data?.files);
            
            // Handle both response.data.files and response.data.data.files structures
            const files = imagesResponse?.data?.data?.files || imagesResponse?.data?.files;
            
            if (files && Array.isArray(files) && files.length > 0) {
              const imageUrls = files.map(file => file.url);
              const fileRecords = files.map((file) => ({ url: file.url, name: file.name }));
              console.log('Carousel image URLs:', imageUrls);
              console.log('Carousel files array:', files);
              setCarousel(prev => ({
                ...prev,
                images: imageUrls,
                files: fileRecords
              }));
            } else {
              console.log('No files found in carousel response');
              console.log('Response structure:', imagesResponse);
              console.log('files value:', files);
              console.log('files type:', typeof files);
              console.log('files isArray:', Array.isArray(files));
              if (files) {
                console.log('files length:', files.length);
              }
            }
          } catch (imageError) {
            console.log('No images found for carousel:', imageError);
          }
        }
      } else {
        console.log('No active carousels found, keeping default images');
      }
    } catch (error) {
      console.error('Error loading carousel:', error);
      console.log('Keeping default images due to error');
    }
  };

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Initialize edit form when opening modal
  const handleEditClick = () => {
    setEditForm({
      slogan: attendanceNote.slogan,
      introduction: attendanceNote.introduction,
      image: attendanceNote.image
    });
    setSelectedFile(null);
    setShowEditModal(true);
  };

  // Handle form changes
  const handleFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditForm(prev => ({
          ...prev,
          image: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  const handleDeleteAttendanceNoteImage = async () => {
    if (!attendanceNote.id || !attendanceNote.imageFileName || isDeletingAttendanceImage) return;
    if (!window.confirm('Remove this image from the info screen?')) return;
    setIsDeletingAttendanceImage(true);
    try {
      await FileService.deletePhysicalFile({
        objectId: attendanceNote.id,
        fileType: 'note_img',
        model: 'attendance_note',
        fileName: attendanceNote.imageFileName
      });
      setAttendanceNote((prev) => ({ ...prev, image: null, imageFileName: null }));
      setEditForm((prev) => ({ ...prev, image: null }));
      setSelectedFile(null);
      await loadAttendanceNote();
    } catch (err) {
      console.error(err);
      window.alert('Could not delete image. Please try again.');
    } finally {
      setIsDeletingAttendanceImage(false);
    }
  };

  // Save changes
  const handleSave = async () => {
    try {
      // First, deactivate the current note if it exists
      if (attendanceNote.id) {
        await AttendanceNoteService.updateAttendanceNote(attendanceNote.id, {
          status: 'inactive',
          update_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
        });
      }
      
      // Create a new attendance note
      const newNoteData = {
        slogan: editForm.slogan,
        introduction: editForm.introduction,
        status: 'active',
        create_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
        update_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
      };
      
      const newNoteResponse = await AttendanceNoteService.createNewAttendanceNote(newNoteData);
      const newNoteId = newNoteResponse.data.id;
      
      // Upload the image file if selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        
        await AttendanceNoteService.uploadAttendanceNoteFile(
          formData,
          newNoteId,
          newNoteId,
          'note_img'
        );
      }
      
      // Reload the attendance note data
      await loadAttendanceNote();
      
      setShowEditModal(false);
    } catch (error) {
      console.error('Error saving attendance note:', error);
      alert('Error saving attendance note. Please try again.');
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setShowEditModal(false);
  };

  // Gallery functions
  const handleGalleryEditClick = () => {
    setGalleryForm([...galleryImages]);
    setShowGalleryModal(true);
  };

  const handleGalleryFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newImages = [];
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newImages.push(e.target.result);
        if (newImages.length === files.length) {
          setGalleryForm(prev => [...prev, ...newImages]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleGallerySave = () => {
    setGalleryImages(galleryForm);
    setShowGalleryModal(false);
  };

  const handleGalleryClose = () => {
    setShowGalleryModal(false);
  };

  const removeGalleryImage = (index) => {
    setGalleryForm(prev => prev.filter((_, i) => i !== index));
  };

  // Carousel functions
  const handleCarouselEditClick = () => {
    const initial =
      carousel.files && carousel.files.length > 0
        ? carousel.files.map((f) => ({ preview: f.url, serverFileName: f.name }))
        : carousel.images.map((url) => ({ preview: url }));
    setCarouselForm(initial);
    setShowGalleryModal(true);
  };

  const handleCarouselFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;

    const readFile = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ preview: reader.result, pendingFile: file });
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

    Promise.all(files.map(readFile))
      .then((items) => {
        setCarouselForm((prev) => {
          const room = CAROUSEL_MAX_IMAGES - prev.length;
          if (room <= 0) return prev;
          const toAdd = items.slice(0, room);
          if (items.length > room) {
            window.alert(
              `Gallery allows at most ${CAROUSEL_MAX_IMAGES} images. Only the first ${room} selected file(s) were added.`
            );
          }
          return [...prev, ...toAdd];
        });
      })
      .catch((err) => {
        console.error(err);
        window.alert('Could not read one or more images. Please try again.');
      });
  };

  const handleCarouselSave = async () => {
    try {
      const pendingFiles = carouselForm.filter((x) => x.pendingFile).map((x) => x.pendingFile);

      // Append new uploads to the existing active carousel so prior images stay (up to CAROUSEL_MAX_IMAGES).
      if (carousel.id) {
        if (pendingFiles.length > 0) {
          for (const file of pendingFiles) {
            const formData = new FormData();
            formData.append('file', file);
            await CarouselService.uploadCarouselFile(
              formData,
              carousel.id,
              carousel.id,
              'carousel_item'
            );
          }
        }
        await loadCarousel();
        setShowGalleryModal(false);
        return;
      }

      if (pendingFiles.length === 0) {
        setShowGalleryModal(false);
        return;
      }

      const newCarouselData = {
        status: 'active',
        create_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
        update_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
      };

      const newCarouselResponse = await CarouselService.createNewCarousel(newCarouselData);
      const newCarouselId = newCarouselResponse.data.id;

      for (const file of pendingFiles) {
        const formData = new FormData();
        formData.append('file', file);
        await CarouselService.uploadCarouselFile(
          formData,
          newCarouselId,
          newCarouselId,
          'carousel_item'
        );
      }

      await loadCarousel();
      setShowGalleryModal(false);
    } catch (error) {
      console.error('Error saving carousel:', error);
      alert('Error saving carousel. Please try again.');
    }
  };

  const removeCarouselImage = async (index) => {
    const item = carouselForm[index];
    if (!item) return;

    if (item.serverFileName && carousel.id) {
      if (!window.confirm('Remove this image from the carousel?')) return;
      setIsDeletingCarouselFile(true);
      try {
        await FileService.deletePhysicalFile({
          objectId: carousel.id,
          fileType: 'carousel_item',
          model: 'carousel',
          fileName: item.serverFileName
        });
        setCarouselForm((prev) => prev.filter((_, i) => i !== index));
        await loadCarousel();
      } catch (err) {
        console.error(err);
        window.alert('Could not delete image. Please try again.');
      } finally {
        setIsDeletingCarouselFile(false);
      }
      return;
    }

    setCarouselForm((prev) => prev.filter((_, i) => i !== index));
  };

  // Background and full screen functions
  const handleBackgroundSelect = () => {
    setShowBackgroundModal(true);
  };

  const handleBackgroundFileUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) {
      setSelectedBackgroundFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setBackgroundImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBackgroundSave = async () => {
    try {
      if (selectedBackgroundFile) {
        const formData = new FormData();
        formData.append('file', selectedBackgroundFile);
        const { data } = await InfoScreenService.uploadBackground(formData);
        const raw = data?.url;
        const display = raw
          ? InfoScreenService.resolveUploadedFileUrl(raw) || committedBackgroundUrl
          : committedBackgroundUrl;
        setCommittedBackgroundUrl(display);
        setBackgroundImage(display);
        setSelectedBackgroundFile(null);
      }
      setShowBackgroundModal(false);
    } catch (error) {
      console.error('Error saving background image:', error);
      const msg = error?.response?.data?.message || 'Error saving background image. Please try again.';
      alert(msg);
    }
  };

  const handleBackgroundClose = () => {
    setBackgroundImage(committedBackgroundUrl);
    setSelectedBackgroundFile(null);
    setShowBackgroundModal(false);
  };

  const openWeatherZipModal = () => {
    setWeatherZipDraft(weatherPayload?.zipcode || '');
    setShowWeatherZipModal(true);
  };

  const handleWeatherZipModalClose = () => {
    setShowWeatherZipModal(false);
  };

  const handleWeatherZipSave = async () => {
    try {
      const { data } = await WeatherService.updateZip(weatherZipDraft.trim());
      setWeatherPayload(data);
      setCookieValueWithMaxAge(
        INFO_SCREEN_WEATHER_CACHE_COOKIE,
        JSON.stringify(data),
        INFO_SCREEN_WEATHER_CACHE_MAX_AGE_SEC
      );
      setShowWeatherZipModal(false);
    } catch (e) {
      window.alert(e?.response?.data?.message || 'Could not save weather location.');
    }
  };

  const handleFullScreen = () => {
    setFullscreenLayout(activeInfoTab);
    setIsFullScreen(true);
  };

  const handleNoticeModalClose = () => {
    setShowNoticeModal(false);
  };

  const handleNoticeSave = async () => {
    try {
      const { data } = await InfoScreenService.updateNotice(noticeDraft);
      setNoticeText(typeof data?.text === 'string' ? data.text : noticeDraft);
      setShowNoticeModal(false);
    } catch (e) {
      window.alert(e?.response?.data?.message || 'Could not save notice.');
    }
  };

  const handleExitFullScreen = () => {
    setIsFullScreen(false);
  };

  // Handle ESC key to exit full screen
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Escape' && isFullScreen) {
        handleExitFullScreen();
      }
    };

    if (isFullScreen) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isFullScreen]);

  useEffect(() => {
    if (customers?.length > 0 && resources?.length > 0) {
      const now = moment();
      const fromDate = new Date(now.year(), now.month(), 1);
      const toDate = new Date(now.year(), now.month() + 1, 0);
      const tomorrow = now.clone().add(1, 'day');
      const appointmentToDate = new Date(tomorrow.year(), tomorrow.month() + 1, 0);
      const needsNextMonthForAppointments = appointmentToDate.getTime() > toDate.getTime();

      Promise.all([
        EventsService.getAllEvents({
          from: EventsService.formatDate(fromDate),
          to: EventsService.formatDate(toDate)
        }),
        EventsService.getAllEventRecurrences(),
        needsNextMonthForAppointments
          ? EventsService.getAllEvents({
              from: EventsService.formatDate(new Date(tomorrow.year(), tomorrow.month(), 1)),
              to: EventsService.formatDate(appointmentToDate)
            })
          : Promise.resolve({ data: [] })
      ]).then(([eventsRes, recurRes, nextMonthRes]) => {
        const allEvents = eventsRes?.data || [];
        const recurrenceRules = (recurRes?.data || []).filter(
          rule => ['medical', 'activity', 'meal_plan'].includes(rule.type) && rule.status === 'active'
        );
        const recurInstances = recurrenceRules.flatMap(rule => expandRecurrence(rule, fromDate, toDate));
        const mergedEvents = [...allEvents, ...recurInstances];

        // Filter medical events (exclude Friendly Reminder provider type/name)
        const isFriendlyReminder = (item) => {
          const matches = (v) => `${v || ''}`.trim().toLowerCase() === 'friendly reminder';
          const resource = (resources || []).find(
            (r) =>
              String(r?.id) === String(item?.data?.resource || '') ||
              String(r?.id) === String(item?.source_uuid || '')
          );
          return (
            matches(item?.source_type) ||
            matches(item?.data?.resource_type) ||
            matches(item?.data?.resource_type_other) ||
            matches(item?.source_name) ||
            matches(item?.data?.resource_name) ||
            matches(resource?.type) ||
            matches(resource?.type_other) ||
            matches(resource?.name)
          );
        };

        const appointmentMergedEvents = needsNextMonthForAppointments
          ? [
              ...allEvents,
              ...(nextMonthRes?.data || []),
              ...recurrenceRules.flatMap((rule) => expandRecurrence(rule, fromDate, appointmentToDate)),
            ]
          : mergedEvents;

        const transportById = new Map(
          appointmentMergedEvents
            .filter((event) => `${event?.type || ''}`.toLowerCase() === 'transportation')
            .map((event) => [String(event.id), event])
        );

        const medicalEventsData = appointmentMergedEvents
          .filter((item) => item.type === 'medical' && item.confirmed && item.status === 'active')
          .filter((item) => !isFriendlyReminder(item))
          .filter((item) => {
            if (!item?.start_time) return false;
            const start = moment(item.start_time);
            return start.isSame(now, 'day') || start.isSame(tomorrow, 'day');
          })
          .sort((a, b) => moment(a.start_time).valueOf() - moment(b.start_time).valueOf())
          .map((item) => {
            const customerId = item?.data?.customer;
            const customerRecord = customerId
              ? customers.find((c) => String(c.id) === String(customerId))
              : customers.find((c) => c?.name === item?.data?.client_name || c?.name === item?.target_name);
            const transportEvent = item?.link_event_uuid
              ? transportById.get(String(item.link_event_uuid))
              : null;
            const driverPickupTime24 = parseDriverPickupTime24(item?.link_event_name || '')
              || (transportEvent?.start_time ? moment(transportEvent.start_time).format('HH:mm') : '');
            return {
              ...item,
              customer: formatInfoScreenAppointmentCustomerName(customerRecord, item?.data),
              driverInfo: formatInfoScreenDriverNameWithPickupTime(item?.link_event_name || '', driverPeople),
              driverName: formatInfoScreenDriverName(item?.link_event_name || '', driverPeople),
              driverPickupTime24,
              startTime: item?.start_time ? moment(item.start_time).format('h:mm A') : '',
              startTime24: item?.start_time ? moment(item.start_time).format('HH:mm') : '',
            };
          });

        const isSameTodayOrTomorrow = (item) => {
          if (!item?.start_time) return false;
          const start = moment(item.start_time);
          return start.isSame(now, 'day') || start.isSame(tomorrow, 'day');
        };

        const activityEventsData = appointmentMergedEvents
          .filter((item) => item.type === 'activity' && item.status === 'active')
          .filter(isSameTodayOrTomorrow)
          .sort((a, b) => moment(a.start_time).valueOf() - moment(b.start_time).valueOf())
          .map((item) => ({
            ...item,
            startTime: item?.start_time ? moment(item.start_time).format('h:mm A') : '',
            startTime24: item?.start_time ? moment(item.start_time).format('HH:mm') : '',
          }));

        const mealPlanEventsData = appointmentMergedEvents.filter(
          (item) => item.type === 'meal_plan' && item.status === 'active' && isSameTodayOrTomorrow(item)
        );

        setMedicalEvents(medicalEventsData);
        setActivityEvents(activityEventsData);
        setMealPlanEvents(mealPlanEventsData);
      }).catch(error => {
        console.error('Error fetching events:', error);
        setMedicalEvents([]);
        setActivityEvents([]);
        setMealPlanEvents([]);
      });
    }
  }, [customers, resources, driverPeople]);

  if (isLoading) {
    return <Spinner animation="border" />;
  }

  return (
    <>
      {!isFullScreen && (
        <div className="list row mb-4">
          <Breadcrumb>
            <Breadcrumb.Item>Info Screen</Breadcrumb.Item>
            <Breadcrumb.Item active>
              Info Screen
            </Breadcrumb.Item>
          </Breadcrumb>
          <div className="col-md-12 text-primary">
            <h4>Info Screen</h4>
          </div>
        </div>
      )}

      <div 
        className={`app-main-content-list-container ${isFullScreen ? 'fullscreen-mode background-image-container' : ''} ${isFullScreen && fullscreenLayout === 'infoScreen2' ? 'info-screen-2-fullscreen' : ''} ${isFullScreen && fullscreenLayout === 'infoScreen3' ? 'info-screen-3-fullscreen' : ''}`}
        style={{
          backgroundImage: isFullScreen ? `url(${backgroundImage})` : 'none'
        }}
      >
        <div className="app-main-content-list-func-container">
          {!isFullScreen && (
            <Tabs
              activeKey={activeInfoTab}
              onSelect={(key) => setActiveInfoTab(key || 'infoScreen')}
              id="info-screen-tab"
            >
              <Tab eventKey="infoScreen" title="Info Screen">
                <div className="multi-columns-container info-screen-page-layout">
                  {canViewAppointmentCalendar && (
                    <div className="column-container">
                      <div className="column-card">
                        <h6 className="text-black" style={{ fontSize: '14px', fontWeight: '600' }}>Appointments{appointmentTitleSuffix}</h6>
                        <div className="app-main-content-fields-section">
                          <div className="list row mb-4">
                            <div className="col-md-12">
                              <table className="personnel-info-table info-screen-appointments-table info-screen-time-col">
                                <thead>
                                  <tr>
                                    <th>Time</th>
                                    <th>Customer</th>
                                    <th>Driver</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {visibleMedicalEvents && visibleMedicalEvents.length > 0 ? (
                                    visibleMedicalEvents.map((medicalEvent, index) => (
                                      <tr key={medicalEvent.id}>
                                        <td>{medicalEvent.startTime}</td>
                                        <td>{medicalEvent.customer}</td>
                                        <td>{medicalEvent.driverInfo}</td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan="3" className="text-center">{appointmentEmptyMessage}</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="column-container">
                    {/* First Piece - Time and Weather Cards */}
                    <div className="time-weather-row mb-4" style={{ gap: '20px' }}>
                      {/* Time Card */}
                      <div className="info-screen-time-weather-item">
                        <div className="card h-100" style={{ borderRadius: '8px' }}>
                          <div className="card-body text-center" style={{ padding: '10px 16px' }}>
                            <div className="mb-1">
                              <div className="text-black" style={{ fontSize: '12px' }}>
                                {moment(currentTime).format('dddd')}
                              </div>
                              <div className="text-black">
                                {moment(currentTime).format('MMM Do, YYYY')}
                              </div>
                              <h5 className="text-primary mt-1 mb-0" style={{ fontSize: '20px', fontWeight: '600' }}>
                                {moment(currentTime).format('HH:mm')}
                              </h5>
                            </div>
                            
                            {/* CSS Clock */}
                            <div className="clock-container" style={{ marginTop: '6px' }}>
                              <div className="clock">
                                <div className="clock-face">
                                  <div className="hand hour-hand" style={{
                                    transform: `rotate(${(moment(currentTime).hour() % 12) * 30 + moment(currentTime).minute() * 0.5}deg)`
                                  }}></div>
                                  <div className="hand minute-hand" style={{
                                    transform: `rotate(${moment(currentTime).minute() * 6}deg)`
                                  }}></div>
                                  <div className="hand second-hand" style={{
                                    transform: `rotate(${moment(currentTime).second() * 6}deg)`
                                  }}></div>
                                  <div className="center-dot"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Weather Card */}
                      <div className="info-screen-time-weather-item">
                        <div className="card h-100" style={{ borderRadius: '8px' }}>
                          <div className="card-body text-center" style={{ padding: '10px 16px' }}>
                            <div className="mb-1">
                              <div className="text-black" style={{ fontSize: '12px' }}>
                                {weatherPayload?.weather?.locationLabel || (weatherPayload?.zipcode ? `ZIP ${weatherPayload.zipcode}` : 'Weather location not set')}
                              </div>
                              <div className="text-black">
                                {weatherPayload?.weather?.condition || '—'}
                              </div>
                              <h5 className="text-primary mt-1 mb-0" style={{ fontSize: '20px', fontWeight: '600' }}>
                                {weatherPayload?.weather?.temperatureC != null
                                  ? `${Math.round(weatherPayload.weather.temperatureC)}°C`
                                  : '—'}
                              </h5>
                            </div>
                            
                            {/* Weather Icon */}
                            <div className="weather-icon" style={{ marginTop: '6px', width: '100%' }}>
                              <i
                                className={weatherPayload?.weather?.iconClass || 'fas fa-cloud'}
                                style={{ fontSize: '120px', color: '#ffc107', lineHeight: '120px', width: '120px', height: '120px' }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Second Piece - AttendanceNote Card */}
                    <div className="mb-4 w-100">
                      <div className="card w-100" style={{ borderRadius: '8px' }}>
                        <div className="card-body">
                          {/* Header with Logo and Edit Button */}
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="d-flex align-items-center">
                              <img src="/images/logo-trans.png" alt="Worldshine Logo" style={{ height: '30px', marginRight: '10px' }} />
                              <strong className="logo-worldshine" style={{ color: '#0066B1', fontSize: '16px' }}>Worldshine</strong>
                            </div>
                            {canEditInfoScreen && (
                              <Button 
                                variant="link" 
                                className="p-0" 
                                onClick={handleEditClick}
                                style={{ color: '#666' }}
                              >
                                <PencilSquare size={16} />
                              </Button>
                            )}
                          </div>
                          
                          {/* Content Layout */}
                          <div className="row">
                            {/* Left Side - Text Content */}
                            <div className="col-md-8">
                              <h5 className="text-primary mb-2">{attendanceNote.slogan}</h5>
                              <p className="text-muted" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                                {attendanceNote.introduction}
                              </p>
                            </div>
                            
                            {/* Right Side - Image */}
                            <div className="col-md-4">
                              <div className="info-attendance-note-media">
                                {attendanceNote.image ? (
                                  <img
                                    src={attendanceNote.image}
                                    alt="Attendance Note"
                                    className="info-attendance-note-img"
                                  />
                                ) : (
                                  <div className="d-flex align-items-center justify-content-center info-attendance-note-placeholder">
                                    <span className="text-muted">No image</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Third Piece - Gallery */}
                    <div className="mb-4 w-100">
                      <div className="card w-100" style={{ borderRadius: '8px' }}>
                        <div className="card-body">
                          {/* Header with Title and Edit Button */}
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="text-black mb-0" style={{ fontSize: '14px', fontWeight: '500' }}>Gallery</h6>
                            {canEditInfoScreen && (
                              <Button 
                                variant="link" 
                                className="p-0" 
                                onClick={handleCarouselEditClick}
                                style={{ color: '#666' }}
                              >
                                <PencilSquare size={16} />
                              </Button>
                            )}
                          </div>
                          
                          {/* Image Carousel */}
                          {carousel.images.length > 0 ? (
                            <Carousel 
                              interval={10000} 
                              controls={false} 
                              indicators={true}
                              style={{ height: '200px' }}
                            >
                              {carousel.images.map((image, index) => (
                                <Carousel.Item key={index}>
                                  <img
                                    className="d-block w-100"
                                    src={image}
                                    alt={`Gallery ${index + 1}`}
                                    style={{ 
                                      height: '200px', 
                                      objectFit: 'cover',
                                      borderRadius: '4px'
                                    }}
                                  />
                                </Carousel.Item>
                              ))}
                            </Carousel>
                          ) : (
                            <div 
                              className="d-flex align-items-center justify-content-center"
                              style={{ 
                                height: '200px', 
                                backgroundColor: '#f8f9fa', 
                                border: '2px dashed #dee2e6',
                                borderRadius: '4px'
                              }}
                            >
                              <span className="text-muted">No images in gallery</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="column-container">
                    <div className="column-card mb-4">
                      <h6 className="text-black mb-3" style={{ fontSize: '14px', fontWeight: '500' }}>Activities{appointmentTitleSuffix}</h6>
                      
                        {/* Activities Table */}
                        
                      <table className="personnel-info-table info-screen-appointments-table"> 
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Activity Name</th>
                            <th>Location</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleActivityEvents && visibleActivityEvents.length > 0 ? (
                            visibleActivityEvents.map((activityEvent, index) => (
                              <tr key={activityEvent.id}>
                                <td>{activityEvent.startTime}</td>
                                <td className="info-screen-multilang-cell">
                                  {stackedLocalizedLines(
                                    activityEvent.title,
                                    activityEvent.title_cn,
                                    activityEvent.title_vn
                                  )}
                                </td>
                                <td>{activityEvent.event_location}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="3" className="text-center">{activityEmptyMessage}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                        
                      
                    </div>
                    <div className="column-card"> 
                      {/* Menu Table */}
                      <div className="list row mb-4">
                        <div className="col-md-12">
                          <h6 className="text-black mb-3" style={{ fontSize: '14px', fontWeight: '500' }}>Menu{appointmentTitleSuffix}</h6>
                          <table className="personnel-info-table info-screen-appointments-table"> 
                            <thead>
                              <tr>
                                <th>Breakfast</th>
                                <th>Lunch</th>
                                <th>Snack</th>
                              </tr>
                            </thead>
                            <tbody>
                              {mealEvents && mealEvents.length > 0 ? (
                                mealEvents.map((mealEvent, index) => (
                                  <tr key={mealEvent.id}>
                                    <td className="info-screen-multilang-cell">{mealEvent.breakfast}</td>
                                    <td className="info-screen-multilang-cell">{mealEvent.lunch}</td>
                                    <td className="info-screen-multilang-cell">{mealEvent.snack}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="3" className="text-center">{mealEmptyMessage}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Tab>
              <Tab eventKey="infoScreen2" title="Info Screen 2">
                <InfoScreen2Layout
                  isFullScreen={false}
                  canViewAppointmentCalendar={canViewAppointmentCalendar}
                  canEditInfoScreen={canEditInfoScreen}
                  medicalEvents={screen2PagedMedicalEvents}
                  appointmentEventsCount={visibleMedicalEvents?.length || 0}
                  appointmentEmptyMessage={appointmentEmptyMessage}
                  appointmentTitleSuffix={appointmentTitleSuffix}
                  appointmentPage={screen2AppointmentsPage}
                  appointmentTotalPages={screen2AppointmentsTotalPages}
                  mealSections={screen2MealSections}
                  activityPageEvents={screen2PagedActivityEvents}
                  activityEventsCount={visibleActivityEvents?.length || 0}
                  activityEmptyMessage={activityEmptyMessage}
                  noticeText={noticeText}
                  currentTime={currentTime}
                  weatherPayload={weatherPayload}
                  carouselImages={carousel.images}
                  onEditNotice={() => {
                    setNoticeDraft(noticeText);
                    setShowNoticeModal(true);
                  }}
                  onEditGallery={handleCarouselEditClick}
                />
              </Tab>
              <Tab eventKey="infoScreen3" title="Info Screen 3">
                <InfoScreen3Layout
                  isFullScreen={false}
                  canViewAppointmentCalendar={canViewAppointmentCalendar}
                  medicalEvents={screen3PagedMedicalEvents}
                  appointmentEventsCount={visibleMedicalEvents?.length || 0}
                  appointmentEmptyMessage={appointmentEmptyMessage}
                  appointmentTitleSuffix={appointmentTitleSuffix}
                  appointmentPage={screen3AppointmentsPage}
                  appointmentTotalPages={screen3AppointmentsTotalPages}
                  mealSections={screen3MealSections}
                  activityPageEvents={screen3PagedActivityEvents}
                  activityEventsCount={visibleActivityEvents?.length || 0}
                  activityEmptyMessage={activityEmptyMessage}
                  currentTime={currentTime}
                  weatherPayload={weatherPayload}
                />
              </Tab>
            </Tabs>
          )}
          
          {/* Full Screen Content */}
          {isFullScreen && fullscreenLayout === 'infoScreen' && (
            <div className="multi-columns-container">
              {canViewAppointmentCalendar && (
                <div className="column-container">
                  <div className="column-card fullscreen-appointments-card">
                    <h6 className="text-black fullscreen-title">Appointments{appointmentTitleSuffix}</h6>
                    <table className="personnel-info-table info-screen-appointments-table fullscreen-appointments-table info-screen-time-col">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Customer</th>
                          <th>Driver</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleMedicalEvents && visibleMedicalEvents.length > 0 ? (
                          fullscreenPagedMedicalEvents.map((medicalEvent, index) => (
                            <tr key={medicalEvent.id}>
                              <td>{medicalEvent.startTime}</td>
                              <td>{medicalEvent.customer}</td>
                              <td>{medicalEvent.driverInfo}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center">{appointmentEmptyMessage}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    {visibleMedicalEvents && visibleMedicalEvents.length > 0 && (
                      <div className="text-muted text-end fullscreen-appointments-pager">
                        Page {fullscreenAppointmentsPage} / {fullscreenAppointmentsTotalPages}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Column 2 - Time, Weather, Attendance Note, Gallery */}
              <div className="column-container">
                {/* Time and Weather Cards Row */}
                <div className="time-weather-row">
                  {/* Time Card */}
                  <div className="col-md-6">
                    <div className="card h-100">
                      <div className="card-body text-center">
                        <div className="fullscreen-date-display">
                          <div className="text-black">{moment(currentTime).format('dddd')}</div>
                          <div className="text-black">{moment(currentTime).format('MMM Do, YYYY')}</div>
                          <h5 className="text-primary mt-2 fullscreen-time">{moment(currentTime).format('HH:mm')}</h5>
                        </div>
                        <div className="clock-container">
                          <div className="clock">
                            <div className="clock-face">
                              <div className="hand hour-hand" style={{
                                transform: `rotate(${(moment(currentTime).hour() % 12) * 30 + moment(currentTime).minute() * 0.5}deg)`
                              }}></div>
                              <div className="hand minute-hand" style={{
                                transform: `rotate(${moment(currentTime).minute() * 6}deg)`
                              }}></div>
                              <div className="hand second-hand" style={{
                                transform: `rotate(${moment(currentTime).second() * 6}deg)`
                              }}></div>
                              <div className="center-dot"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Weather Card */}
                  <div className="col-md-6">
                    <div className="card h-100">
                      <div className="card-body text-center">
                        <div className="fullscreen-date-display">
                          <div className="text-black">
                            {weatherPayload?.weather?.locationLabel || (weatherPayload?.zipcode ? `ZIP ${weatherPayload.zipcode}` : 'Weather location not set')}
                          </div>
                          <div className="text-black">{weatherPayload?.weather?.condition || '—'}</div>
                          <h5 className="text-primary mt-2 fullscreen-time">
                            {weatherPayload?.weather?.temperatureC != null
                              ? `${Math.round(weatherPayload.weather.temperatureC)}°C`
                              : '—'}
                          </h5>
                        </div>
                        <div className="weather-icon">
                          <i className={weatherPayload?.weather?.iconClass || 'fas fa-cloud'} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* AttendanceNote Card */}
                <div className="attendance-note-wrapper">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="d-flex align-items-center">
                          <img src="/images/logo-trans.png" alt="Worldshine Logo" className="fullscreen-logo" />
                          <strong className="logo-worldshine">Worldshine</strong>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-8">
                          <h5 className="text-primary mb-2">{attendanceNote.slogan}</h5>
                          <p className="text-muted fullscreen-intro">{attendanceNote.introduction}</p>
                        </div>
                        <div className="col-md-4">
                          <div className="fullscreen-note-media">
                            {attendanceNote.image ? (
                              <img src={attendanceNote.image} alt="Attendance Note" className="img-fluid rounded fullscreen-note-img" />
                            ) : (
                              <div className="d-flex align-items-center justify-content-center fullscreen-placeholder">
                                <span className="text-muted">No image</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Gallery Card */}
                <div className="gallery-wrapper">
                  <div className="card">
                    <div className="card-body">
                      <h6 className="text-black mb-2 fullscreen-title">Gallery</h6>
                      {carousel.images.length > 0 ? (
                        <Carousel 
                          interval={carousel.images.length > 1 ? 5000 : null} 
                          controls={false} 
                          indicators={carousel.images.length > 1} 
                          className="fullscreen-carousel"
                        >
                          {carousel.images.map((image, index) => (
                            <Carousel.Item key={index}>
                              <img className="d-block w-100 fullscreen-carousel-img" src={image} alt={`Gallery ${index + 1}`} />
                            </Carousel.Item>
                          ))}
                        </Carousel>
                      ) : (
                        <div className="d-flex align-items-center justify-content-center fullscreen-placeholder">
                          <span className="text-muted">No images in gallery</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Column 3 - Activities and Menu */}
              <div className="column-container">
                <div className="column-card">
                  <h6 className="text-black fullscreen-title">Activities{appointmentTitleSuffix}</h6>
                  <table className="personnel-info-table info-screen-appointments-table"> 
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Activity</th>
                        <th>Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleActivityEvents && visibleActivityEvents.length > 0 ? (
                        visibleActivityEvents.map((activityEvent, index) => (
                          <tr key={activityEvent.id}>
                            <td>{activityEvent.startTime}</td>
                            <td className="info-screen-multilang-cell">
                              {stackedLocalizedLines(
                                activityEvent.title,
                                activityEvent.title_cn,
                                activityEvent.title_vn
                              )}
                            </td>
                            <td>{activityEvent.event_location}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center">{activityEmptyMessage}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="column-card"> 
                  <h6 className="text-black fullscreen-title">Menu{appointmentTitleSuffix}</h6>
                  <table className="personnel-info-table info-screen-appointments-table"> 
                    <thead>
                      <tr>
                        <th>Breakfast</th>
                        <th>Lunch</th>
                        <th>Snack</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mealEvents && mealEvents.length > 0 ? (
                        mealEvents.map((mealEvent, index) => (
                          <tr key={mealEvent.id}>
                            <td className="info-screen-multilang-cell">{mealEvent.breakfast}</td>
                            <td className="info-screen-multilang-cell">{mealEvent.lunch}</td>
                            <td className="info-screen-multilang-cell">{mealEvent.snack}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center">{mealEmptyMessage}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {isFullScreen && fullscreenLayout === 'infoScreen2' && (
            <InfoScreen2Layout
              isFullScreen
              canViewAppointmentCalendar={canViewAppointmentCalendar}
              canEditInfoScreen={canEditInfoScreen}
              medicalEvents={screen2PagedMedicalEvents}
              appointmentEventsCount={visibleMedicalEvents?.length || 0}
              appointmentEmptyMessage={appointmentEmptyMessage}
              appointmentTitleSuffix={appointmentTitleSuffix}
              appointmentPage={screen2AppointmentsPage}
              appointmentTotalPages={screen2AppointmentsTotalPages}
              mealSections={screen2MealSections}
              activityPageEvents={screen2PagedActivityEvents}
              activityEventsCount={visibleActivityEvents?.length || 0}
              activityEmptyMessage={activityEmptyMessage}
              noticeText={noticeText}
              currentTime={currentTime}
              weatherPayload={weatherPayload}
              carouselImages={carousel.images}
              onEditNotice={() => {}}
              onEditGallery={() => {}}
            />
          )}

          {isFullScreen && fullscreenLayout === 'infoScreen3' && (
            <InfoScreen3Layout
              isFullScreen
              canViewAppointmentCalendar={canViewAppointmentCalendar}
              medicalEvents={screen3PagedMedicalEvents}
              appointmentEventsCount={visibleMedicalEvents?.length || 0}
              appointmentEmptyMessage={appointmentEmptyMessage}
              appointmentTitleSuffix={appointmentTitleSuffix}
              appointmentPage={screen3AppointmentsPage}
              appointmentTotalPages={screen3AppointmentsTotalPages}
              mealSections={screen3MealSections}
              activityPageEvents={screen3PagedActivityEvents}
              activityEventsCount={visibleActivityEvents?.length || 0}
              activityEmptyMessage={activityEmptyMessage}
              currentTime={currentTime}
              weatherPayload={weatherPayload}
            />
          )}
          
          {!isFullScreen && (
            <div className="list-func-panel">
              {canEditInfoScreen && (
                <button className="btn btn-primary me-2" type="button" onClick={openWeatherZipModal}>
                  <GeoAlt size={16} className="me-2" />Change weather location
                </button>
              )}
              {canEditInfoScreen && (
                <button className="btn btn-primary me-2" onClick={handleBackgroundSelect}>
                  <FileImage size={16} className="me-2"></FileImage>Choose Background
                </button>
              )}
              <button className="btn btn-primary me-2" onClick={handleFullScreen}>
                <ArrowsFullscreen size={16} className="me-2"></ArrowsFullscreen>Full Screen
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Edit AttendanceNote Modal */}
      {canEditInfoScreen && <Modal show={showEditModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Attendance Note</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            {/* Left Side - Text Fields */}
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Slogan</label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.slogan}
                  onChange={(e) => handleFormChange('slogan', e.target.value)}
                  placeholder="Enter slogan"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Introduction</label>
                <textarea
                  className="form-control"
                  rows="4"
                  value={editForm.introduction}
                  onChange={(e) => handleFormChange('introduction', e.target.value)}
                  placeholder="Enter introduction text"
                />
              </div>
            </div>
            
            {/* Right Side - File Upload */}
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Image</label>
                <div 
                  className="border rounded p-4 text-center"
                  style={{ 
                    border: '2px dashed #dee2e6',
                    backgroundColor: '#f8f9fa',
                    cursor: 'pointer'
                  }}
                  onClick={() => document.getElementById('imageUpload').click()}
                >
                  {editForm.image ? (
                    <div>
                      <img 
                        src={editForm.image} 
                        alt="Preview" 
                        className="img-fluid rounded mb-2"
                        style={{ maxHeight: '150px' }}
                      />
                      <p className="text-muted mb-0">Click to change image</p>
                    </div>
                  ) : (
                    <div>
                      <i className="fas fa-cloud-upload-alt" style={{ fontSize: '48px', color: '#6c757d' }}></i>
                      <p className="text-muted mb-0">Click to upload</p>
                    </div>
                  )}
                  <input
                    id="imageUpload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                </div>
                {attendanceNote.imageFileName && !selectedFile && editForm.image && (
                  <Button
                    type="button"
                    variant="outline-danger"
                    size="sm"
                    className="mt-2"
                    disabled={isDeletingAttendanceImage}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAttendanceNoteImage();
                    }}
                  >
                    <Trash size={14} className="me-1" />
                    {isDeletingAttendanceImage ? 'Removing…' : 'Delete uploaded image'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>}

      {/* Gallery Edit Modal */}
      {canEditInfoScreen && <Modal show={showGalleryModal} onHide={handleGalleryClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Update Images</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {carouselForm.length < CAROUSEL_MAX_IMAGES && (
            <div className="mb-3">
              <label className="form-label">Upload Images</label>
              <p className="text-muted small mb-2">
                Up to {CAROUSEL_MAX_IMAGES} images total ({carouselForm.length} / {CAROUSEL_MAX_IMAGES}).
              </p>
              <div
                className="border rounded p-4 text-center"
                style={{
                  border: '2px dashed #dee2e6',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                }}
                onClick={() => document.getElementById('galleryUpload').click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    document.getElementById('galleryUpload').click();
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <i className="fas fa-cloud-upload-alt" style={{ fontSize: '48px', color: '#6c757d' }}></i>
                <p className="text-muted mb-0">Click to upload multiple images</p>
                <input
                  id="galleryUpload"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleCarouselFileUpload}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          )}
          {carouselForm.length >= CAROUSEL_MAX_IMAGES && (
            <p className="text-muted small mb-3">
              Maximum of {CAROUSEL_MAX_IMAGES} images reached. Remove an image to upload more.
            </p>
          )}
          
          {/* Current Images */}
          {carouselForm.length > 0 && (
            <div className="mb-3">
              <label className="form-label">Current Images</label>
              <div className="row">
                {carouselForm.map((item, index) => (
                  <div key={`carousel-img-${index}`} className="col-md-4 mb-2">
                    <div className="position-relative">
                      <img 
                        src={item.preview} 
                        alt={`Gallery ${index + 1}`} 
                        className="img-fluid rounded"
                        style={{ height: '100px', width: '100%', objectFit: 'cover' }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute"
                        style={{ top: '5px', right: '5px' }}
                        disabled={isDeletingCarouselFile}
                        title="Remove image"
                        onClick={() => removeCarouselImage(index)}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleGalleryClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCarouselSave}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>}

      {/* Weather ZIP (per site) */}
      {canEditInfoScreen && (
        <Modal show={showWeatherZipModal} onHide={handleWeatherZipModalClose} size="md">
          <Modal.Header closeButton>
            <Modal.Title>Change weather location</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>ZIP code (US)</Form.Label>
              <Form.Control
                type="text"
                value={weatherZipDraft}
                onChange={(e) => setWeatherZipDraft(e.target.value)}
                placeholder="e.g. 20850 or 20850-1234"
              />
              <Form.Text className="text-muted">
                Saved for this site only. The weather tile uses this location.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleWeatherZipModalClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleWeatherZipSave}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Background Selection Modal */}
      {canEditInfoScreen && <Modal show={showBackgroundModal} onHide={handleBackgroundClose} size="md">
        <Modal.Header closeButton>
          <Modal.Title>Choose Background Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Upload Background Image</label>
            <div 
              className="border rounded p-4 text-center"
              style={{ 
                border: '2px dashed #dee2e6',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer'
              }}
              onClick={() => document.getElementById('backgroundUpload').click()}
            >
              {selectedBackgroundFile ? (
                <div>
                  <img 
                    src={backgroundImage} 
                    alt="Background Preview" 
                    className="img-fluid rounded mb-2"
                    style={{ maxHeight: '200px', maxWidth: '100%' }}
                  />
                  <p className="text-muted mb-0">Click to change background image</p>
                </div>
              ) : (
                <div>
                  <i className="fas fa-image" style={{ fontSize: '48px', color: '#6c757d' }}></i>
                  <p className="text-muted mb-0">Click to upload background image</p>
                </div>
              )}
              <input
                id="backgroundUpload"
                type="file"
                accept="image/*"
                onChange={handleBackgroundFileUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleBackgroundClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleBackgroundSave}>
            Save Background
          </Button>
        </Modal.Footer>
      </Modal>}

      {canEditInfoScreen && (
        <Modal show={showNoticeModal} onHide={handleNoticeModalClose} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Edit Notice / 公告栏</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Notice text</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={noticeDraft}
                onChange={(e) => setNoticeDraft(e.target.value)}
                placeholder="Enter notice text"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleNoticeModalClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleNoticeSave}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Full Screen Hint */}
      {isFullScreen && (
        <div className="fullscreen-hint">
          Press ESC to exit full screen
        </div>
      )}
    </>
  );
};

export default InfoScreen; 